'use client';

import React, { FormEvent } from 'react'
import Link from "next/link"
import { useRouter } from 'next/navigation'
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Facebook, Twitter, Linkedin, Instagram } from "lucide-react"
import { useToast } from "@/components/hooks/use-toast"

const Footer: React.FC = () => {
  const router = useRouter()
  const { toast } = useToast()

  const handleNewsletterSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    toast({
      title: "Newsletter Subscription",
      description: "We thank you for the interest but Newsletters will commence post commercial launch of the Eworld Platform. Nonetheless, we invite you to subscribe to the waitlist here to get priority access to trial the platform in Beta mode (once it is ready) and help shape the future of business.",
      duration: 8000,
    })

    router.push('/contact-us?subscribe=true')
  }

  return (
    <footer className="bg-[#5f4664] text-white py-6 text-xs">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0 lg:pr-8">
          <div>
            <h3 className="text-sm font-semibold mb-3">Sitemap</h3>
            <ul className="space-y-1">
              <li><Link href="/what-is-eworld" className="hover:text-[#c39bd3] transition-colors">What is EWORLD</Link></li>
              <li><Link href="/why-eworld" className="hover:text-[#c39bd3] transition-colors">Why EWORLD</Link></li>
              <li><Link href="/pricing" className="hover:text-[#c39bd3] transition-colors">Pricing</Link></li>
              <li><Link href="/our-partners" className="hover:text-[#c39bd3] transition-colors">Our Partners</Link></li>
              <li><Link href="/about-us" className="hover:text-[#c39bd3] transition-colors">About Us</Link></li>
              <li><Link href="/investors" className="hover:text-[#c39bd3] transition-colors">Investors</Link></li>
              <li><Link href="/media" className="hover:text-[#c39bd3] transition-colors">Media</Link></li>
              <li><Link href="/contact-us" className="hover:text-[#c39bd3] transition-colors">Contact Us</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold mb-3">Legal</h3>
            <ul className="space-y-1">
              <li><Link href="/legal?tab=privacy" className="hover:text-[#c39bd3] transition-colors">Privacy Policy</Link></li>
              <li><Link href="/legal?tab=terms" className="hover:text-[#c39bd3] transition-colors">Terms of Service</Link></li>
            </ul>
            <h3 className="text-sm font-semibold mt-4 mb-2">Contact</h3>
            <div className="space-y-1">
              <p className="text-xs grid grid-cols-[60px_1fr] items-start">
                <span>Email:</span>
                <span>ceooffice@eworld.global</span>
              </p>
              <p className="text-xs grid grid-cols-[60px_1fr] items-center">
                <span>Phone:</span>
                <span className="flex items-center">
                  +65 9823-2944
                  <Link href="https://wa.me/6598232944" className="ml-2">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#25D366">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </Link>
                </span>
              </p>
              <p className="text-xs grid grid-cols-[60px_1fr] items-start">
                <span>Address:</span>
                <span>
                  32, Pekin Street, #05-01,
                  <br />
                  Singapore 048762
                </span>
              </p>
            </div>
          </div>
          <div className="lg:mr-8">
            <h3 className="text-sm font-semibold mb-3">Follow Us</h3>
            <div className="flex space-x-4 mb-4">
              <Link href="#" className="hover:text-[#c39bd3] transition-colors"><Facebook size={16} /></Link>
              <Link href="#" className="hover:text-[#c39bd3] transition-colors"><Twitter size={16} /></Link>
              <Link href="#" className="hover:text-[#c39bd3] transition-colors"><Linkedin size={16} /></Link>
              <Link href="#" className="hover:text-[#c39bd3] transition-colors"><Instagram size={16} /></Link>
            </div>
            <h3 className="text-sm font-semibold mb-2">Newsletter</h3>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col space-y-2">
              <Input type="email" placeholder="Your email" className="bg-white text-black text-xs h-7" required />
              <Button type="submit" className="bg-[#c39bd3] hover:bg-[#b185c0] text-white h-7 text-xs">Subscribe</Button>
            </form>
          </div>
          <div className="md:col-span-2 lg:col-span-1">
            <h3 className="text-sm font-semibold mb-3">About EWORLD</h3>
            <p className="text-xs mb-3">EWORLD is a sophisticated, AI and blockchain-powered global marketplace that seamlessly integrates all components of business value chains across industries worldwide.</p>
            <Link href="/what-is-eworld" className="text-[#c39bd3] hover:underline text-xs">Learn More</Link>
          </div>
        </div>
        <div className="mt-6 pt-6 border-t border-gray-700 text-center text-[10px]">
          <p>&copy; {new Date().getFullYear()} EWORLD Marketplace. All rights reserved.</p>
        </div>
      </div>
    </footer>
  )
}

export default Footer